import React, { FormEvent, useCallback, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import api from "../../api"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { RootState } from "../../store"
import { editBag, resetSuccess } from "../../store/linehaul.store"
import ConsolAccordionBody from "../Consolidations/ConsolAccordionBody"
import ReadyParcelsTable from "./ReadyParcelsTable"

const voidFn = () => {}

const BagModal = ({
    show,
    setShow,
    warehouse,
    errors,
    setErrors,
    setSelectedRows,
    options,
    setOptions,
    showMessage
}: {
    show: string
    setShow: Function
    warehouse: number
    errors: Record<string, any>
    setErrors: Function
    setSelectedRows: Function
    selectedRows: number[]
    options: Record<string, any>
    setOptions: Function
    showMessage: (message: string, variant: string, title: string) => void
}) => {
    const { readyLHParcels, country, editingBag, destinationsList, destinationsLoading, destinationsError } =
        useAppSelector((state: RootState) => state.linehaul)
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [boxWeightAndDims, setBoxWeightAndDims] = useState<Record<string, any>>({})
    const [selectedBoxes, setSelectedBoxes] = useState<string[]>([])
    const [remarks, setRemarks] = useState<string>("")
    const [bag_type, setBagType] = useState("")
    const [final_destination, setFinalDestination] = useState(country)
    const [agent_detail, setAgentDetails] = useState("")
    const [messageTextAreaLength, setMessageTextAreaLength] = useState(500 - remarks?.length || 500)
    const [readyLHParcelsEdit, setReadyLHParcelsEdit] = useState(readyLHParcels?.parcels ?? [])
    const [dimsAndWeightUnit, setDimsAndWeightUnit] = useState({
        weightUnit: "kg",
        dimsUnit: "cm"
    } as Record<string, any>)

    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState<boolean>(false)

    const dimsAndWeight: Record<string, any> = {
        height: {
            dimValue: boxWeightAndDims?.height,
            unitValue: dimsAndWeightUnit.dimsUnit,
            units: ["cm"]
        },
        width: {
            dimValue: boxWeightAndDims?.width,
            unitValue: dimsAndWeightUnit.dimsUnit,
            units: ["cm"]
        },
        length: {
            dimValue: boxWeightAndDims?.length,
            unitValue: dimsAndWeightUnit.dimsUnit,
            units: ["cm"]
        },
        weight: {
            dimValue: boxWeightAndDims?.weight,
            unitValue: dimsAndWeightUnit.weightUnit,
            units: ["kg"]
        },
        ch_weight: {
            dimValue: boxWeightAndDims?.ch_weight,
            unitValue: dimsAndWeightUnit.weightUnit,
            units: ["kg"]
        }
    }

    const checkRequiredFields = (data: Record<string, any>) => {
        let safe = true
        let fieldsWithIssues = {} as Record<string, any>
        Object.values(data).forEach((f, index) => {
            if (!f || f?.length === 0 || parseFloat(f) === 0) {
                safe = false
                if (Object.keys(data)[index]) {
                    fieldsWithIssues[`${Object.keys(data)[index]}`] =
                        "This field is required, and must be more than 0"
                }
            }
        })
        return {
            status: safe,
            issues: fieldsWithIssues
        }
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (show === "deleteBag" && editingBag.id) {
            try {
                setSubmitting(true)
                const response = await api.linehaul.deleteBag({
                    bagId: editingBag.id
                })
                if (!response?.data?.status) throw new Error(response.data?.message ?? "Unable to delete bag")
                setSubmitting(false)
                showMessage(response.data?.message ?? "Bag deleted successfully", "success", "Success")
                handleClose()
                dispatch(resetSuccess(true))
            } catch (e: any) {
                setSubmitting(false)
                showMessage(e.message ?? "Unable to update bag", "danger", "Error")
                handleClose()
                dispatch(resetSuccess(true))
            }
            return
        }
        const { ch_weight, height, length, weight, width } = boxWeightAndDims
        const { dimsUnit, weightUnit } = dimsAndWeightUnit
        let validity = checkRequiredFields({
            height,
            length,
            weight,
            width,
            dimsUnit,
            weightUnit,
            bag_type,
            ...((bag_type ?? "").toLocaleLowerCase() === "transit" ? { final_destination } : {})
        })
        if (validity?.status) {
            setSubmitting(true)
            if (show === "updateBag" && editingBag) {
                const bagData = {
                    warehouse,
                    destination: country,
                    height,
                    length,
                    width,
                    dimensions_unit: dimsUnit,
                    weight_unit: weightUnit,
                    actual_weight_value: weight,
                    ch_weight_value: ch_weight,
                    remarks,
                    parcels: selectedBoxes,
                    bag_type,
                    agent_detail,
                    ...((bag_type ?? "").toLocaleLowerCase() === "transit" ? { final_destination } : {}),
                    id: editingBag.id
                }
                try {
                    setSubmitting(true)
                    const response = await api.linehaul.updateBag({
                        bagData
                    })
                    if (!response?.data?.status)
                        throw new Error(response.data?.message ?? "Unable to update bag")
                    setSubmitting(false)
                    showMessage(response.data?.message ?? "Bag updated successfully", "success", "Success")
                    handleClose()
                    const lhURL = response.data.payload.lh_bag_url ?? ""
                    if (lhURL) window.open(lhURL, "_blank", "noreferrer")
                    dispatch(resetSuccess(true))
                    // reloadAfterSubmit()
                } catch (e: any) {
                    setSubmitting(false)
                    showMessage(e.message ?? "Unable to update bag", "danger", "Error")
                    handleClose()
                    dispatch(resetSuccess(undefined))
                }
            } else {
                const bagData = {
                    warehouse,
                    destination: country,
                    height,
                    length,
                    width,
                    dimensions_unit: dimsUnit,
                    weight_unit: weightUnit,
                    actual_weight_value: weight,
                    ch_weight_value: ch_weight,
                    remarks,
                    parcels: selectedBoxes,
                    bag_type,
                    agent_detail,
                    ...((bag_type ?? "").toLocaleLowerCase() === "transit" ? { final_destination } : {})
                }
                try {
                    const response = await api.linehaul.createBag({
                        bagData
                    })
                    if (!response?.data?.status)
                        throw new Error(response.data?.message ?? "Unable to create a bag")
                    setSubmitting(false)
                    showMessage(response?.data?.message ?? "Bag created successfully", "success", "Success")
                    handleClose()
                    const lhURL = response.data.payload.lh_bag_url ?? ""
                    if (lhURL) window.open(lhURL, "_blank", "noreferrer")
                    dispatch(resetSuccess(true))
                } catch (e: any) {
                    setSubmitting(false)
                    showMessage(e.message ?? "Unable to create a bag", "danger", "Error")
                    handleClose()
                    dispatch(resetSuccess(undefined))
                }
            }
        } else {
            setErrors({
                ...(validity?.issues || {})
            })
        }
    }

    const handleClose = useCallback(() => {
        setShow("")
        setSelectedRows([])
        dispatch(editBag(undefined))
        setErrors({})
    }, [dispatch, setShow])

    useEffect(() => {
        const handleAsyncLogic = async () => {
            if (show === "updateBag" && editingBag) {
                setLoading(true)
                try {
                    const response = await api.linehaul.bagDetails({
                        bagId: editingBag.bag_id
                    })
                    if (!response?.data?.status || !response.data.payload)
                        throw new Error(response?.data?.message ?? "Unable to get bag data")
                    const {
                        parcels,
                        height,
                        width,
                        length,
                        dimensions_unit,
                        actual_weight_value,
                        ch_weight_value,
                        weight_unit,
                        remarks,
                        bag_type,
                        final_destination,
                        agent_detail
                    } = response?.data?.payload
                    setSelectedBoxes((parcels ?? []).map((parcel: any) => parcel.id))
                    setReadyLHParcelsEdit((parcels ?? []).concat(readyLHParcels?.parcels ?? []))
                    setBoxWeightAndDims({
                        ch_weight: ch_weight_value ?? 0,
                        height: height ?? 0,
                        length: length ?? 0,
                        weight: actual_weight_value ?? 0,
                        width: width ?? 0
                    })
                    setDimsAndWeightUnit({
                        weightUnit: weight_unit ?? "kg",
                        dimsUnit: dimensions_unit ?? "cm"
                    })
                    setRemarks(remarks ?? "")
                    setBagType(bag_type)
                    setFinalDestination(final_destination)
                    setAgentDetails(agent_detail)
                    setLoading(false)
                } catch (e: any) {
                    setLoading(false)
                    showMessage(e.message ?? "Unable to get bag data", "danger", "Error")
                    dispatch(resetSuccess(undefined))
                    handleClose()
                }
            }
        }
        handleAsyncLogic()
    }, [])

    const showDestination = (bag_type ?? "").toLocaleLowerCase() === "transit" ? true : false

    return (
        <Modal
            size={show === "deleteBag" ? "lg" : "xl"}
            dialogClassName={show === "deleteBag" ? "" : "xxl-modal"}
            show={["createBag", "updateBag", "deleteBag"].includes(show)}
            onHide={handleClose}
            onClose={handleClose}
            backdrop="static"
            scrollable
            keyboard={false}>
            <Modal.Header closeButton={!submitting}>
                <Modal.Title className="text-uppercase">
                    {show === "updateBag"
                        ? `BAG # ${editingBag.bag_id}`
                        : show === "deleteBag"
                          ? `${t("Delete")} ${editingBag.bag_id}`
                          : t("Create Bag")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-gray-bg">
                {loading || destinationsLoading ? (
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only"></span>
                        </div>
                    </div>
                ) : destinationsError ? (
                    <p className="text-danger">Unable to load destinations</p>
                ) : (
                    <Row>
                        <Col>
                            {show !== "deleteBag" ? (
                                <div className="main-container modal-gray-bg">
                                    <ReadyParcelsTable
                                        data={
                                            editingBag && show === "updateBag"
                                                ? readyLHParcelsEdit
                                                : (readyLHParcels?.parcels ?? [])
                                        }
                                        setSelectedRows={setSelectedBoxes}
                                        selectedRows={selectedBoxes}
                                        options={options}
                                        setOptions={setOptions}
                                    />
                                </div>
                            ) : null}
                            <Form onSubmit={handleSubmit} id="bag-form" className="font-13">
                                {show === "deleteBag" ? (
                                    <Row className="px-1 align-items-start">
                                        <Col>Are you sure you want to delete</Col>
                                    </Row>
                                ) : (
                                    <>
                                        <Row className="px-1 align-items-start">
                                            {Object.keys(dimsAndWeight)?.map((key: string) => (
                                                <Col key={key} xs={12} md={6} lg>
                                                    <ConsolAccordionBody
                                                        anyStep
                                                        dimValue={dimsAndWeight[key].dimValue}
                                                        dimFieldName={key}
                                                        unitFieldName={
                                                            key === "weight" ? "weightUnit" : "dimsUnit"
                                                        }
                                                        unitValue={dimsAndWeight[key].unitValue}
                                                        dimsAndWeightUnit={dimsAndWeightUnit}
                                                        setDimsAndWeightUnit={setDimsAndWeightUnit}
                                                        boxWeightAndDims={boxWeightAndDims}
                                                        setBoxWeightAndDims={setBoxWeightAndDims}
                                                        units={dimsAndWeight[key].units}
                                                        setWeightLimitExceeded={voidFn}
                                                        setSelectedBoxes={setSelectedBoxes}
                                                        boxes={selectedBoxes}
                                                        errors={errors}
                                                        setErrors={setErrors}
                                                        warehouse={warehouse}
                                                        pallet={true}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                        <Row className="px-1 align-items-start">
                                            <Col xs={12} lg={3}>
                                                <div className="mb-3">
                                                    <div className="mb-2">
                                                        Type&nbsp;
                                                        <span className="text-danger fw-bolder">*</span>
                                                    </div>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            inline
                                                            label="Direct"
                                                            type="radio"
                                                            checked={bag_type === "direct"}
                                                            onChange={() => setBagType("direct")}
                                                            isInvalid={!!errors.bag_type}
                                                        />
                                                        <Form.Check
                                                            inline
                                                            label="Transit"
                                                            type="radio"
                                                            checked={bag_type === "transit"}
                                                            onChange={() => setBagType("transit")}
                                                            isInvalid={!!errors.bag_type}
                                                        />
                                                        {!!errors.bag_type ? (
                                                            <Form.Control.Feedback
                                                                type="invalid"
                                                                className="d-block">
                                                                You must select an option
                                                            </Form.Control.Feedback>
                                                        ) : null}
                                                    </Form.Group>
                                                </div>
                                            </Col>
                                            {showDestination ? (
                                                <Col xs={12} lg={3}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label htmlFor="destination">
                                                            {t("Final Destination")}
                                                        </Form.Label>
                                                        <Form.Select
                                                            value={final_destination}
                                                            onChange={(e) =>
                                                                setFinalDestination(e.target.value)
                                                            }
                                                            isInvalid={!!errors.final_destination}
                                                            disabled={!!final_destination}>
                                                            <>
                                                                {!final_destination ? (
                                                                    <option
                                                                        key="noneSelected"
                                                                        value=""></option>
                                                                ) : null}
                                                                {destinationsList
                                                                    .filter(
                                                                        (destination) =>
                                                                            destination.iso2_code === country
                                                                    )
                                                                    .map((destination) => (
                                                                        <option
                                                                            key={destination.id}
                                                                            value={destination.iso2_code}>
                                                                            {destination.name}
                                                                        </option>
                                                                    ))}
                                                            </>
                                                        </Form.Select>
                                                        {!!errors.final_destination ? (
                                                            <Form.Control.Feedback type="invalid">
                                                                You must select an option
                                                            </Form.Control.Feedback>
                                                        ) : null}
                                                    </Form.Group>
                                                </Col>
                                            ) : null}

                                            <Col>
                                                <Form.Group className="mb-3">
                                                    <Form.Label htmlFor="clearanceAgentDetails">
                                                        {t("Clearance agent details")}
                                                    </Form.Label>
                                                    <Form.Control
                                                        value={agent_detail}
                                                        name={"clearanceAgentDetails"}
                                                        onChange={(e) => setAgentDetails(e.target.value)}
                                                        placeholder="Clearance agent details"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="px-1 justify-content-center align-items-center">
                                            <Col>
                                                <Form.Group className="mb-3 w-100">
                                                    <Form.Label htmlFor="remarks">{t("Remarks")}</Form.Label>
                                                    <Form.Control
                                                        value={remarks}
                                                        maxLength={500}
                                                        as="textarea"
                                                        name={"remarks"}
                                                        autoComplete="off"
                                                        onChange={(e: any) => {
                                                            let textAreaValue = e.target.value
                                                            setMessageTextAreaLength(
                                                                500 - textAreaValue?.length
                                                            )
                                                            setRemarks(textAreaValue)
                                                        }}
                                                        placeholder="Remarks..."
                                                    />
                                                    <div className="float-end">
                                                        <p
                                                            className={`mt-2 ${
                                                                messageTextAreaLength <= 0 && "text-danger"
                                                            }`}>
                                                            {messageTextAreaLength} {t("Chars left")}
                                                        </p>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Form>
                        </Col>
                    </Row>
                )}
                <Row className="px-1 mt-3">
                    <Col className="d-flex align-items-center justify-content-end">
                        <Button
                            type="button"
                            disabled={submitting || loading}
                            className="float-end mb-2 gray-bg px-3 mx2"
                            onClick={handleClose}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            disabled={submitting || loading}
                            type="submit"
                            form="bag-form"
                            className="float-end mb-2 primary-bg px-3 mx-2">
                            {submitting || loading ? (
                                <div className="text-center">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="sr-only"></span>
                                    </div>
                                </div>
                            ) : show === "deleteBag" ? (
                                t("Delete")
                            ) : (
                                t("Save")
                            )}
                        </Button>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    )
}

export default BagModal
